import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { radioStations, type RadioStation } from '@/mocks/radios';
import { useAudioPlayer } from '@/context/AudioContext';
import { radioHealthData, getDirectoryPriorityScore } from '@/mocks/radioHealth';

interface FeaturedRadiosProps {
  activeCategory: string;
  onCategoryChange: (category: string) => void;
}

function formatListeners(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return `${n}`;
}

function RadioCard({ station }: { station: RadioStation }) {
  const navigate = useNavigate();
  const { isPlaying, currentStation, play, pause } = useAudioPlayer();
  const isThisPlaying = isPlaying && currentStation?.id === station.id;

  const handlePlay = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isThisPlaying) {
      pause();
    } else {
      play(station);
    }
  };

  const handleEnter = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/radio/${station.frequency.replace('.', '-')}`);
  };

  return (
    <div
      className="group bg-background-50 rounded-2xl p-4 md:p-5 border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer flex flex-col"
      data-product-shop
      onClick={handleEnter}
    >
      <div className="flex items-start gap-3 mb-3">
        <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl overflow-hidden bg-background-100 shrink-0 flex items-center justify-center border border-background-200">
          <img
            src={station.logoUrl}
            alt={station.name}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="font-semibold text-foreground-950 text-sm md:text-base truncate">{station.name}</h3>
          <p className="text-foreground-500 text-xs md:text-sm truncate">{station.city}, {station.state}</p>
        </div>
        {station.isLive && (
          <div className="shrink-0 flex items-center gap-1 bg-accent-100 text-accent-700 px-2 py-0.5 rounded-full text-[10px] font-bold">
            <span className="w-1 h-1 bg-accent-500 rounded-full"></span>
            LIVE
          </div>
        )}
      </div>

      <div className="flex items-center justify-between mt-auto pt-3 border-t border-background-100">
        <div>
          <span className="text-primary-600 font-bold text-lg md:text-xl tabular-nums">{station.frequency}</span>
          <span className="text-primary-400 font-semibold text-xs ml-1">RFW</span>
        </div>
        <span className="text-foreground-400 text-xs flex items-center gap-1">
          <i className="ri-headphone-line text-xs"></i>
          {formatListeners(station.listeners)}
        </span>
      </div>

      <div className="mt-3 flex gap-2">
        <button
          onClick={handlePlay}
          className={`flex-1 py-2 text-background-50 text-xs font-semibold rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer flex items-center justify-center gap-1 ${
            isThisPlaying
              ? 'bg-accent-500 hover:bg-accent-600'
              : 'bg-primary-500 hover:bg-primary-600'
          }`}
        >
          <i className={`text-sm ${isThisPlaying ? 'ri-pause-fill' : 'ri-play-fill'}`}></i>
          {isThisPlaying ? 'Pausar' : 'Escuchar'}
        </button>
        <button
          onClick={handleEnter}
          className="flex-1 py-2 bg-background-100 hover:bg-background-200 text-foreground-700 text-xs font-semibold rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer flex items-center justify-center gap-1"
        >
          Entrar
          <i className="ri-arrow-right-line text-sm"></i>
        </button>
      </div>
    </div>
  );
}

export default function FeaturedRadios({ activeCategory, onCategoryChange }: FeaturedRadiosProps) {
  const navigate = useNavigate();

  const allCategories = useMemo(() => {
    const cats = new Set(radioStations.map((r) => r.category));
    return Array.from(cats).sort();
  }, []);

  const sortedStations = useMemo(() => {
    const healthMap = new Map(radioHealthData.map((h) => [h.radioId, h]));

    return [...radioStations]
      .map((station) => {
        const health = healthMap.get(station.id);
        const priority = health ? getDirectoryPriorityScore(health) : 0;
        return { station, priority, health };
      })
      .filter(({ health }) => {
        if (!health) return true;
        return !health.isHiddenFromDirectory && health.healthStatus !== 'suspendida';
      })
      .sort((a, b) => b.priority - a.priority)
      .map(({ station }) => station);
  }, []);

  const filtered = activeCategory === 'Todas'
    ? sortedStations
    : sortedStations.filter((r) => r.category === activeCategory);

  return (
    <section id="destacadas" className="py-16 md:py-24 bg-background-100">
      <div className="w-full px-4 md:px-8 lg:px-12 max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-semibold mb-4">
              <i className="ri-star-line text-xs"></i>
              Radios Destacadas
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground-950 tracking-tight">
              Estaciones que están<br />
              <span className="text-primary-600">sonando ahora</span>
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/explorar')}
              className="text-accent-600 hover:text-accent-700 font-semibold text-sm flex items-center gap-1 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-compass-3-line"></i>
              Descubrir
            </button>
            <button
              onClick={() => navigate('/radios')}
              className="text-primary-600 hover:text-primary-700 font-semibold text-sm flex items-center gap-1 whitespace-nowrap cursor-pointer"
            >
              Ver todas las radios
              <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2 scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          <button
            onClick={() => onCategoryChange('Todas')}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
              activeCategory === 'Todas'
                ? 'bg-primary-500 text-background-50 shadow-lg shadow-primary-500/20'
                : 'bg-background-50 text-foreground-600 hover:text-foreground-950 hover:bg-background-200 border border-background-200'
            }`}
          >
            Todas
          </button>
          {allCategories.map((cat) => (
            <button
              key={cat}
              onClick={() => onCategoryChange(cat)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                activeCategory === cat
                  ? 'bg-primary-500 text-background-50 shadow-lg shadow-primary-500/20'
                  : 'bg-background-50 text-foreground-600 hover:text-foreground-950 hover:bg-background-200 border border-background-200'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
          {filtered.map((station) => (
            <RadioCard key={station.id} station={station} />
          ))}
        </div>
      </div>
    </section>
  );
}